import React from 'react';
import PropTypes from 'prop-types';
import {
  formatValue,
  WIDE_CARD_ENTRY_THRESHOLD,
  COMPOSITE_DOOR_GEARING_GROUP,
  HARDWARE_PACKING_GROUP,
  COMPOSITE_DOOR_PACKING_GROUP,
  groupShape,
  areaShape
} from './outputPerformanceShared';
import { StationEntry } from './StationEntry';
import { buildDirectApiStationView, buildPackingStationView } from './packingLogic';

function CompositeDoorGearingCard({ areaName, group, items, onSelectStation }) {
  const station = buildDirectApiStationView(items, group);
  const entry = {
    label: station.name,
    value: station.total,
    isResolved: station.isResolved,
    station
  };

  return (
    <div
      className="output-benchmark-card output-benchmark-card--list"
      data-source={station.source}
    >
      <div className="output-benchmark-card-title">{group.name}</div>
      <div className="output-benchmark-entry-grid is-two-col">
        <StationEntry
          entry={entry}
          onClick={() =>
            onSelectStation({ areaName, groupName: group.name, station })
          }
        />
      </div>
    </div>
  );
}

CompositeDoorGearingCard.propTypes = {
  areaName: PropTypes.string.isRequired,
  group: groupShape.isRequired,
  items: PropTypes.array.isRequired,
  onSelectStation: PropTypes.func.isRequired
};

function HardwarePackingCard({ areaName, group, items, onSelectStation }) {
  const stationConfigs =
    (group.stations || []).length > 0
      ? group.stations
      : [{ name: group.name, displayType: group.displayType, isResolved: true }];

  const packingMetricType = group.name === COMPOSITE_DOOR_PACKING_GROUP ? 'compositeDoorBoxes' : 'hardwareBoxes';
  const stationViews = stationConfigs.map(sc => buildPackingStationView(items, sc, packingMetricType));
  const entries = stationViews.map(station => ({
    label: station.name,
    value: station.total,
    hasBoxData: false,
    hasWeldedCillData: true,
    weldedCillBoxesCreated: station.weldedCillBoxesCreated,
    weldedCillTotalFacets: station.weldedCillTotalFacets,
    weldedCillFacetsLabel: station.weldedCillFacetsLabel,
    isResolved: station.isResolved,
    station
  }));

  const cardSource = stationViews.some(s => s.source === 'api') ? 'api' : 'none';

  return (
    <div
      className="output-benchmark-card output-benchmark-card--list"
      data-source={cardSource}
    >
      <div className="output-benchmark-card-title">{group.name}</div>
      <div className="output-benchmark-entry-grid is-two-col">
        {entries.map(entry => (
          <StationEntry
            key={`${group.name}-${entry.label}`}
            entry={entry}
            onClick={() =>
              onSelectStation({ areaName, groupName: group.name, station: entry.station })
            }
          />
        ))}
      </div>
    </div>
  );
}

HardwarePackingCard.propTypes = {
  areaName: PropTypes.string.isRequired,
  group: groupShape.isRequired,
  items: PropTypes.array.isRequired,
  onSelectStation: PropTypes.func.isRequired
};

function GroupBenchmarkCard({ areaName, group, onSelectStation }) {
  const entries = group.stations.map(station => ({
    label: station.name,
    value: station.total,
    hasBoxData: Boolean(station.hasBoxData),
    itemsCount: station.itemsCount || 0,
    boxesCount: station.boxesCount || 0,
    hasWeldedCillData: Boolean(station.hasWeldedCillData),
    weldedCillBoxesCreated: station.weldedCillBoxesCreated || 0,
    weldedCillTotalFacets: station.weldedCillTotalFacets || 0,
    itemTypeBreakdown: station.itemTypeBreakdown || null,
    isResolved: station.isResolved !== false,
    station
  }));

  const isWide = entries.length >= WIDE_CARD_ENTRY_THRESHOLD;
  const splitIndex = Math.ceil(entries.length / 2);
  const leftEntries = entries.slice(0, splitIndex);
  const rightEntries = entries.slice(splitIndex);

  if (isWide) {
    return (
      <div
        className="output-benchmark-card output-benchmark-card--wide"
        data-source={group.source}
      >
        <div className="output-benchmark-card-title">{group.name}</div>
        <div className="output-benchmark-wide-grid">
          <div className="output-benchmark-entry-grid is-two-col">
            {leftEntries.map(entry => (
              <StationEntry
                key={`${group.name}-left-${entry.label}`}
                entry={entry}
                onClick={() =>
                  onSelectStation({ areaName, groupName: group.name, station: entry.station })
                }
              />
            ))}
          </div>
          <div className="output-benchmark-entry-grid is-two-col">
            {rightEntries.map(entry => (
              <StationEntry
                key={`${group.name}-right-${entry.label}`}
                entry={entry}
                onClick={() =>
                  onSelectStation({ areaName, groupName: group.name, station: entry.station })
                }
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="output-benchmark-card output-benchmark-card--list"
      data-source={group.source}
    >
      <div className="output-benchmark-card-title">{group.name}</div>
      <div className="output-benchmark-entry-grid is-two-col">
        {entries.map(entry => (
          <StationEntry
            key={`${group.name}-${entry.label}`}
            entry={entry}
            onClick={() =>
              onSelectStation({ areaName, groupName: group.name, station: entry.station })
            }
          />
        ))}
      </div>
    </div>
  );
}

GroupBenchmarkCard.propTypes = {
  areaName: PropTypes.string.isRequired,
  group: groupShape.isRequired,
  onSelectStation: PropTypes.func.isRequired
};

export function AreaSection({ area, onSelectStation, compositeDoorGearingItems, hardwarePackingItems }) {
  return (
    <section className="output-area-section" data-source={area.source}>
      <div className="output-area-section-header">
        <div className="output-area-section-title">{area.name}</div>
        <div className="output-area-section-total">
          <span>Total</span>
          <strong>{formatValue(area.total)}</strong>
        </div>
      </div>

      <div className="output-benchmark-grid output-benchmark-grid--groups">
        {area.groups.map(group => {
          if (group.name === COMPOSITE_DOOR_GEARING_GROUP) {
            return (
              <CompositeDoorGearingCard
                key={group.name}
                areaName={area.name}
                group={group}
                items={compositeDoorGearingItems}
                onSelectStation={onSelectStation}
              />
            );
          }
          if (
            group.name === HARDWARE_PACKING_GROUP ||
            group.name === COMPOSITE_DOOR_PACKING_GROUP
          ) {
            return (
              <HardwarePackingCard
                key={group.name}
                areaName={area.name}
                group={group}
                items={hardwarePackingItems}
                onSelectStation={onSelectStation}
              />
            );
          }
          return (
            <GroupBenchmarkCard
              key={group.name}
              areaName={area.name}
              group={group}
              onSelectStation={onSelectStation}
            />
          );
        })}
      </div>
    </section>
  );
}

AreaSection.propTypes = {
  area: areaShape.isRequired,
  onSelectStation: PropTypes.func.isRequired,
  compositeDoorGearingItems: PropTypes.array.isRequired,
  hardwarePackingItems: PropTypes.array.isRequired
};
